import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartLinesUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    event.promise
      ?.then(async ({ cart, detail }) => {
        /* AYUNEL OVERRIDE(2026-09-02 智鹏拍板「赠品不占购物车数量」):
           取数优先级改为非赠品口径,与 SSR(cart-bubble/隐藏锚)三位一体:
           ① detail.items = 原始 AJAX items(带 properties)→ 过滤 _ayn_gift 求和(最准)
           ② detail.itemCount = SSR 隐藏锚喂的数(已是非赠品口径)
           ③ 都没有(如 remove-all 路径)→ 现拉 /cart.js 过滤(双脑复审 Codex 补:
              直接用 cart.totalQuantity 会退回含赠品数)
           ④ 全失败且事件有 totalQuantity 时用它兜底;事件也未知则保留上次确认数,绝不假造空车。 */
        // 2026-09-13:客人从赠品商品页手动加的 ¥0「(ギフト専用)」行(没标记)也不算 —— gift-sync 会清,气泡别先跳一下
        const nongift = (items) =>
          items.reduce(
            (sum, it) =>
              (it.properties && (it.properties._ayn_gift === '1' || it.properties._ayn_teacher_code)) ||
              (it.price === 0 && /ギフト専用/.test(it.product_title || it.title || ''))
                ? sum
                : sum + (it.quantity || 0),
            0
          );
        let itemCount;
        if (Array.isArray(detail?.items)) {
          itemCount = nongift(detail.items);
        } else if (typeof detail?.itemCount === 'number') {
          itemCount = detail.itemCount;
        } else {
          try {
            const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
            if (!r.ok) throw new Error('Cart count unavailable');
            const ajaxCart = await r.json();
            if (!Array.isArray(ajaxCart.items)) throw new Error('Cart count missing items');
            itemCount = nongift(ajaxCart.items);
          } catch {
            // AYUNEL OVERRIDE 2026-10-03: successful add may have no confirmed full cart after a failed GET.
            // Keep the visible/session count until a real cart arrives; unknown is not zero.
            if (typeof cart?.totalQuantity !== 'number') return;
            itemCount = cart.totalQuantity;
          }
        }

        this.renderCartBubble(itemCount);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] Event promise rejected:', error);
      });
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The absolute number of items in the cart.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
